import { useState, type ChangeEvent } from "react";
import { readSheet } from "read-excel-file/browser";
import { Link } from "../app/navigation";
import { useWorkspace } from "../app/workspace";
import { AppShell } from "../components/AppShell";
import { WorkspaceGate } from "../components/WorkspaceGate";
import type { Role } from "../domain/types";
import { roleLabels } from "./OperationsPages";

const roleOptions: Role[] = [
  "manager",
  "instructor",
  "desk",
  "accounting",
  "read_only",
];
type ImportRow = {
  displayName: string;
  schoolGrade: string;
  guardianContactMasked: string;
  billingPlan: "monthly" | "session";
  remainingSessions?: number;
};
type Preview = {
  fileName: string;
  rows: ImportRow[];
  rejected: Array<{ row: number; reason: string; source: string[] }>;
  headers: string[];
};

export function TeamSettingsPage() {
  return (
    <WorkspaceGate roles={["owner", "sample_user"]}>
      <TeamSettings />
    </WorkspaceGate>
  );
}

function TeamSettings() {
  const { state, command, mutating } = useWorkspace();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    displayName: "",
    role: "instructor" as Role,
    scopeClassIds: [] as string[],
  });
  async function prepare() {
    await command({
      type: "team.invitationPrepare",
      displayName: form.displayName,
      role: form.role,
      scopeClassIds: form.scopeClassIds,
    });
    setShow(false);
  }
  return (
    <AppShell
      title="팀과 권한"
      description="역할과 담당 수업 범위를 최소 권한으로 관리합니다"
      actions={
        <button
          type="button"
          className="primary-action"
          onClick={() => setShow((value) => !value)}
        >
          구성원 권한 준비
        </button>
      }
    >
      {show ? (
        <section className="editor-panel">
          <header>
            <p>외부 발송 없음</p>
            <h2>구성원 권한 초안</h2>
          </header>
          <div className="form-grid">
            <label>
              표시 이름
              <input
                value={form.displayName}
                onChange={(event) =>
                  setForm({ ...form, displayName: event.target.value })
                }
              />
            </label>
            <label>
              역할
              <select
                value={form.role}
                onChange={(event) =>
                  setForm({ ...form, role: event.target.value as Role })
                }
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {roleLabels[role]}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className="scope-field">
              <legend>볼 수 있는 수업</legend>
              {state!.classes.map((item) => (
                <label key={item.id}>
                  <input
                    type="checkbox"
                    checked={form.scopeClassIds.includes(item.id)}
                    onChange={() =>
                      setForm({
                        ...form,
                        scopeClassIds: form.scopeClassIds.includes(item.id)
                          ? form.scopeClassIds.filter((id) => id !== item.id)
                          : [...form.scopeClassIds, item.id],
                      })
                    }
                  />{" "}
                  {item.name}
                </label>
              ))}
            </fieldset>
          </div>
          <footer>
            <p>
              이 단계는 권한만 저장합니다. 이메일 초대나 계정 생성은 하지
              않습니다.
            </p>
            <button
              type="button"
              className="primary-action"
              disabled={mutating || form.displayName.trim().length < 2}
              onClick={() => void prepare()}
            >
              권한 초안 저장
            </button>
          </footer>
        </section>
      ) : null}
      <section className="data-sheet">
        <div className="data-sheet__head team-head">
          <span>구성원</span>
          <span>역할</span>
          <span>수업 범위</span>
          <span>초대 상태</span>
          <span>권한 원칙</span>
        </div>
        {state!.teamMembers.map((member) => (
          <article className="team-head" key={member.id}>
            <span>
              <strong>{member.displayName}</strong>
              <small>{member.state === "active" ? "계정 연결됨" : "권한 초안"}</small>
            </span>
            <span>{roleLabels[member.role]}</span>
            <span>
              {member.scopeClassIds.length
                ? member.scopeClassIds
                    .map(
                      (id) =>
                        state!.classes.find((item) => item.id === id)?.name ??
                        id,
                    )
                    .join(", ")
                : member.role === "owner"
                  ? "전체"
                  : "아직 없음"}
            </span>
            <span>
              {member.state === "active"
                ? "사용 중"
                : member.state === "invitation_prepared"
                  ? "초안 · 미발송"
                  : "사용 중지"}
            </span>
            <span>
              {member.role === "owner"
                ? "재인증 없는 이전 금지"
                : "담당 수업만"}
            </span>
          </article>
        ))}
      </section>
      <section className="policy-callout">
        <strong>원장 권한 이전</strong>
        <p>
          양쪽 재인증과 되돌리기 영수증이 필요한 고위험 작업이라 현재 일반
          설정에서는 실행하지 않습니다.
        </p>
      </section>
    </AppShell>
  );
}

export function ImportPage() {
  return (
    <WorkspaceGate roles={["owner", "manager", "sample_user"]}>
      <ImportStudents />
    </WorkspaceGate>
  );
}

function ImportStudents() {
  const { state, command, mutating } = useWorkspace();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reading, setReading] = useState(false);
  const [parseError, setParseError] = useState("");
  async function choose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setReading(true);
    setParseError("");
    setPreview(null);
    try {
      if (file.size > 2_000_000)
        throw new Error("파일은 2MB 이하로 나눠 주세요.");
      const matrix = file.name.toLocaleLowerCase("ko-KR").endsWith(".csv")
        ? parseCsv(await file.text())
          : (await readSheet(file)).map((row) =>
            row.map((cell) =>
              cell === null
                ? ""
                : cell instanceof Date
                  ? cell.toISOString()
                  : String(cell),
            ),
          );
      setPreview(buildPreview(file.name, matrix));
    } catch (error) {
      setParseError(
        error instanceof Error ? error.message : "파일을 읽지 못했어요.",
      );
    } finally {
      setReading(false);
    }
  }
  async function commit() {
    if (!preview) return;
    await command({
      type: "import.students",
      fileName: preview.fileName,
      rows: preview.rows,
      rejectedRows: preview.rejected.length,
    });
    setPreview(null);
  }
  return (
    <AppShell
      title="학생 명단 가져오기"
      description="원본 파일은 이 브라우저에서만 읽고, 확인한 정상 행만 저장합니다"
      actions={
        <Link className="secondary-action" href="/students">
          학생 목록
        </Link>
      }
    >
      <section className="import-workbench">
        <div className="import-drop">
          <span>쉼표 구분 파일 · 엑셀 파일</span>
          <h2>명단 파일을 이 기기에서 확인합니다.</h2>
          <p>
            지원 열: 학생 이름, 학년, 보호자 연락처 표식, 수강 방식, 남은 회차
          </p>
          <label className="primary-action">
            {reading ? "파일 읽는 중…" : "파일 선택"}
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(event) => void choose(event)}
              disabled={reading}
            />
          </label>
          <small>2MB 이하 · 최대 500명 · 업로드 전 미리보기</small>
        </div>
        {parseError ? (
          <p className="inline-error" role="alert">
            {parseError}
          </p>
        ) : null}
        {preview ? (
          <article className="import-preview">
            <header>
              <div>
                <p>{preview.fileName}</p>
                <h2>가져오기 전 대조</h2>
              </div>
              <dl>
                <div>
                  <dt>정상</dt>
                  <dd>{preview.rows.length}</dd>
                </div>
                <div>
                  <dt>오류</dt>
                  <dd>{preview.rejected.length}</dd>
                </div>
                <div>
                  <dt>현재 학생</dt>
                  <dd>{state!.students.length}</dd>
                </div>
              </dl>
            </header>
            <div className="mapping-strip">
              {preview.headers.map((header, index) => (
                <span key={`${header}-${index}`}>
                  {header || `${index + 1}열`}
                </span>
              ))}
            </div>
            <div className="preview-table">
              <div>
                <span>이름</span>
                <span>학년</span>
                <span>보호자 표식</span>
                <span>방식</span>
                <span>회차</span>
              </div>
              {preview.rows.slice(0, 8).map((row, index) => (
                <div key={`${row.displayName}-${index}`}>
                  <span>{row.displayName}</span>
                  <span>{row.schoolGrade}</span>
                  <span>{row.guardianContactMasked}</span>
                  <span>
                    {row.billingPlan === "session" ? "회차제" : "월정액"}
                  </span>
                  <span>{row.remainingSessions ?? "—"}</span>
                </div>
              ))}
            </div>
            {preview.rejected.length ? (
              <details>
                <summary>오류 {preview.rejected.length}행과 이유</summary>
                <ul>
                  {preview.rejected.slice(0, 30).map((item) => (
                    <li key={`${item.row}-${item.reason}`}>
                      <strong>{item.row}행</strong> {item.reason}
                      <code>{item.source.join(" | ")}</code>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="text-action"
                  onClick={() => downloadRejected(preview)}
                >
                  오류 행 CSV 받기
                </button>
              </details>
            ) : null}
            <footer>
              <p>
                중복은 이름+보호자 표식으로 다시 확인하며, 저장 뒤 영수증에서 한
                번에 되돌릴 수 있습니다.
              </p>
              <button
                type="button"
                className="primary-action"
                disabled={mutating || preview.rows.length === 0}
                onClick={() => void commit()}
              >
                {mutating
                  ? "원장에 기록 중…"
                  : `정상 ${preview.rows.length}명 가져오기`}
              </button>
            </footer>
          </article>
        ) : null}
      </section>
      {state!.importReceipts.length ? (
        <section className="paper-section">
          <header>
            <p>되돌릴 수 있는 기록</p>
            <h2>가져오기 영수증</h2>
          </header>
          <ol className="receipt-list">
            {state!.importReceipts.toReversed().map((receipt) => (
              <li key={receipt.id}>
                <div>
                  <strong>{receipt.fileName}</strong>
                  <span>
                    정상 {receipt.acceptedRows} · 오류 {receipt.rejectedRows} ·
                    중복 {receipt.duplicateRows}
                  </span>
                </div>
                <b>{receipt.state === "committed" ? "저장됨" : "되돌림"}</b>
                <button
                  type="button"
                  className="secondary-action"
                  disabled={mutating || receipt.state === "rolled_back"}
                  onClick={() =>
                    void command({
                      type: "import.rollback",
                      receiptId: receipt.id,
                    })
                  }
                >
                  이 가져오기 되돌리기
                </button>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </AppShell>
  );
}

export function DataSettingsPage() {
  return (
    <WorkspaceGate roles={["owner", "sample_user"]}>
      <DataSettings />
    </WorkspaceGate>
  );
}

function DataSettings() {
  const { state, command, mutating } = useWorkspace();
  const [confirmation, setConfirmation] = useState("");
  const active = state!.dataRequests.find(
    (item) => item.kind === "deletion" && item.state === "received",
  );
  return (
    <AppShell
      title="데이터와 내보내기"
      description="구독 상태와 무관하게 전체 기록을 가져갈 수 있습니다"
      actions={
        <a href="/api/export" download className="primary-action">
          전체 기록 내려받기
        </a>
      }
    >
      <section className="data-map">
        <article>
          <span>운영 원장</span>
          <strong>학생·수업·출결·회차</strong>
          <p>원인 사건과 수정 영수증을 포함합니다.</p>
        </article>
        <article>
          <span>돈 인접 기록</span>
          <strong>청구·수동 입금·수당 후보</strong>
          <p>실제 PG·계좌·송금 완료와 구분합니다.</p>
        </article>
        <article>
          <span>대화와 지원</span>
          <strong>보호자 초안·가린 문의</strong>
          <p>원문 연락처와 자유 메모는 기본 내보내기 범위에서 분리합니다.</p>
        </article>
      </section>
      <section className="retention-table">
        <header>
          <p>공개 보존 원칙 · 2026-08-26 시행</p>
          <h2>종류별 보존과 삭제</h2>
        </header>
        <div>
          <strong>계정·권한</strong>
          <span>이용 중 + 탈퇴 처리 기간</span>
          <span>권한 감사와 복구</span>
        </div>
        <div>
          <strong>운영 원장</strong>
          <span>계약 종료 뒤 삭제 요청 일정</span>
          <span>무료 내보내기 우선</span>
        </div>
        <div>
          <strong>결제·계약 증빙</strong>
          <span>적용 법령의 의무 기간</span>
          <span>일반 운영 데이터와 분리</span>
        </div>
        <div>
          <strong>샘플</strong>
          <span>12시간</span>
          <span>자동 만료</span>
        </div>
      </section>
      <section className="danger-zone">
        <header>
          <p>되돌릴 수 있는 대기 기간</p>
          <h2>서비스 데이터 삭제 요청</h2>
        </header>
        {active ? (
          <div className="deletion-receipt">
            <strong>삭제 요청 접수됨</strong>
            <p>
              {new Intl.DateTimeFormat("ko-KR", { dateStyle: "long" }).format(
                new Date(active.scheduledFor),
              )}{" "}
              예정
            </p>
            <span>{active.legalHoldNotice}</span>
            <button
              type="button"
              className="secondary-action"
              disabled={mutating}
              onClick={() =>
                void command({
                  type: "data.deletionCancel",
                  requestId: active.id,
                })
              }
            >
              삭제 요청 취소
            </button>
          </div>
        ) : (
          <>
            <p>
              요청을 저장해도 즉시 삭제하지 않습니다. 전체 내보내기를 먼저 받고
              30일 동안 취소할 수 있습니다.
            </p>
            <label>
              확인을 위해 학원 이름 입력
              <input
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                placeholder={state!.tenant.name}
              />
            </label>
            <button
              type="button"
              className="danger-action"
              disabled={mutating || confirmation !== state!.tenant.name}
              onClick={() =>
                void command({ type: "data.deletionRequest", confirmation })
              }
            >
              삭제 요청 접수
            </button>
          </>
        )}
      </section>
    </AppShell>
  );
}

export function PlanSettingsPage() {
  return (
    <WorkspaceGate roles={["owner", "sample_user"]}>
      <PlanSettings />
    </WorkspaceGate>
  );
}

function PlanSettings() {
  const { state } = useWorkspace();
  return (
    <AppShell
      title="요금과 사용권"
      description="공개 초기 운영 기간에는 결제 없이 핵심 기능을 사용합니다"
    >
      <section className="trial-banner">
        <div>
          <span>초기 공개 운영</span>
          <h2>현재 결제 없이 이용 중</h2>
          <p>가격·결제·자동 갱신은 활성화하지 않았습니다.</p>
        </div>
        <dl>
          <div>
            <dt>공급자</dt>
            <dd>
              {state!.trial.providerState === "disabled"
                ? "연결 전"
                : state!.trial.providerState}
            </dd>
          </div>
          <div>
            <dt>결제</dt>
            <dd>비활성</dd>
          </div>
        </dl>
      </section>
      <section className="plan-grid" aria-label="현재 이용 범위">
        <article>
          <span>수업 운영</span>
          <h2>계속 사용할 수 있어요</h2>
          <ul>
            <li>오늘 수업·출결·보강과 마감</li>
            <li>수동 수납·수당 후보 대조</li>
            <li>역할별 학원 운영 기록</li>
          </ul>
        </article>
        <article>
          <span>데이터 권리</span>
          <h2>요금과 분리합니다</h2>
          <ul>
            <li>전체 기록 내려받기</li>
            <li>가져오기 반영 되돌리기</li>
            <li>계정·학원 데이터 삭제 요청</li>
          </ul>
        </article>
        <article>
          <span>아직 열지 않은 기능</span>
          <h2>동작하는 척하지 않아요</h2>
          <ul>
            <li>실제 결제·자동 갱신</li>
            <li>보호자 메시지 자동 발송</li>
            <li>외부 상담 도구 전달</li>
          </ul>
        </article>
      </section>
      <p className="boundary-note boundary-note--large">
        향후 유료 기능을 열 때에는 가격·제공 범위·환불·갱신 조건을 먼저 고지하고
        별도 동의를 받습니다. 기존 내보내기·삭제 권리는 유료 전환으로 회수하지
        않습니다.
      </p>
    </AppShell>
  );
}

function buildPreview(fileName: string, matrix: string[][]): Preview {
  if (matrix.length < 2)
    throw new Error("머리글과 학생 행이 있는 파일을 선택해 주세요.");
  const headers = matrix[0]!.map((cell) => cell.trim());
  const aliases = {
    displayName: ["학생 이름", "이름", "학생명", "name"],
    schoolGrade: ["학년", "grade"],
    guardianContactMasked: ["보호자 연락처 표식", "보호자", "연락처", "phone"],
    billingPlan: ["수강 방식", "요금 방식", "billing"],
    remainingSessions: ["남은 회차", "회차", "sessions"],
  } as const;
  const index = Object.fromEntries(
    Object.entries(aliases).map(([key, values]) => [
      key,
      headers.findIndex((header) =>
        values.some(
          (alias) =>
            header.toLocaleLowerCase("ko-KR") ===
            alias.toLocaleLowerCase("ko-KR"),
        ),
      ),
    ]),
  ) as Record<keyof typeof aliases, number>;
  if (index.displayName < 0)
    throw new Error(
      "학생 이름 열을 찾지 못했어요. 머리글을 “학생 이름”으로 바꿔 주세요.",
    );
  const rows: ImportRow[] = [];
  const rejected: Preview["rejected"] = [];
  matrix.slice(1).forEach((source, offset) => {
    if (source.every((cell) => !cell.trim())) return;
    const values = source.map((cell) => cell.trim());
    const risky = values.find((cell) => /^[=+\-@]/.test(cell));
    const displayName = values[index.displayName] ?? "";
    const rawPlan =
      index.billingPlan >= 0 ? (values[index.billingPlan] ?? "") : "월정액";
    const billingPlan = /회차|session/i.test(rawPlan)
      ? "session"
      : /월|month/i.test(rawPlan)
        ? "monthly"
        : null;
    const rawRemaining =
      index.remainingSessions >= 0
        ? (values[index.remainingSessions] ?? "")
        : "";
    const remaining = rawRemaining === "" ? undefined : Number(rawRemaining);
    let reason = "";
    if (risky) reason = "수식으로 실행될 수 있는 값이 있어요.";
    else if (displayName.length < 2 || displayName.length > 40)
      reason = "학생 이름은 2–40자여야 해요.";
    else if (!billingPlan)
      reason = "수강 방식은 월정액 또는 회차제로 적어 주세요.";
    else if (
      billingPlan === "session" &&
      (remaining === undefined ||
        !Number.isSafeInteger(remaining) ||
        remaining < 0)
    )
      reason = "회차제 학생의 남은 회차는 0 이상의 정수여야 해요.";
    if (reason || !billingPlan) {
      rejected.push({
        row: offset + 2,
        reason: reason || "행을 읽지 못했어요.",
        source: values,
      });
      return;
    }
    const row: ImportRow = {
      displayName,
      schoolGrade:
        index.schoolGrade >= 0
          ? values[index.schoolGrade] || "미지정"
          : "미지정",
      guardianContactMasked:
        index.guardianContactMasked >= 0
          ? values[index.guardianContactMasked] || "연락처 미등록"
          : "연락처 미등록",
      billingPlan,
    };
    if (billingPlan === "session") row.remainingSessions = remaining ?? 0;
    rows.push(row);
  });
  if (rows.length > 500)
    throw new Error("정상 행이 500명을 넘어요. 파일을 나눠 주세요.");
  return { fileName, rows, rejected, headers };
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]!;
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (character === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += character;
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function downloadRejected(preview: Preview) {
  const safe = (value: string) =>
    `"${(/^[=+\-@]/.test(value) ? `'${value}` : value).replaceAll('"', '""')}"`;
  const body = [
    ["행", "오류", ...preview.headers].map(safe).join(","),
    ...preview.rejected.map((item) =>
      [String(item.row), item.reason, ...item.source].map(safe).join(","),
    ),
  ].join("\r\n");
  const url = URL.createObjectURL(
    new Blob([`\uFEFF${body}`], { type: "text/csv;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "수업마침-가져오기-오류.csv";
  anchor.click();
  URL.revokeObjectURL(url);
}
